import { Injector, Directive } from '@angular/core'
import { filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators'
import { TranslateService } from '@ngx-translate/core'

import { DialogService } from '@shared/dialogs/dialog.service'
import { ApiTableComponent } from '@shared/components/tables/api-table.component'
import { DocumentRow, DocumentStatusLabel, DocumentStatus } from './document.model'
import { DocumentService } from './document.service'
import { FormControl } from '@angular/forms'

@Directive()
export abstract class DocumentList extends ApiTableComponent {
  isProcessing: { [id: string]: boolean } = {}
  DocumentStatusLabel = DocumentStatusLabel

  constructor(
    private dialogService: DialogService,
    private documentService: DocumentService,
    private translateService: TranslateService,
    protected injector: Injector,
  ) {
    super(injector)
  }

  deleteDocument(document: DocumentRow) {
    const { id } = document
    const message = this.translateService.instant('CLIENT.VEHICLES.DOCUMENTS.DELETE_CONFIRMATION', { value: document.title }) as string

    this.dialogService
      .confirm(message)
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => (this.isProcessing[id] = true)),
        switchMap(() => this.documentService.delete(id)),
        switchMap(() => this.dataSource.refresh()),
        finalize(() => (this.isProcessing[id] = false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe()
  }

  archiveDocument(document: DocumentRow): void {
    const message = this.translateService.instant('CLIENT.VEHICLES.DOCUMENTS.ARCHIVE_CONFIRMATION', { value: document.title }) as string
    this.dialogService
      .confirm(message)
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => (this.isProcessing[document.id] = true)),
        switchMap(() => this.documentService.archive(document.id)),
        switchMap(() => this.dataSource.refresh()),
        finalize(() => (this.isProcessing[document.id] = false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe()
  }

  restoreDocument(id: number): void {
    this.isProcessing[id] = true
    this.documentService
      .restore(id)
      .pipe(
        switchMap(() => this.dataSource.refresh()),
        finalize(() => (this.isProcessing[id] = false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe()
  }

  isDeletePossible(document: DocumentRow) {
    return document.status !== DocumentStatus.Deleted
  }

  isArchivePossible(document: DocumentRow) {
    return document.status !== DocumentStatus.Archive
  }

  displayRemainingDays(days: number | null) {
    if (days === null) return '-'
    return this.translateService.instant('CLIENT.VEHICLES.SERVICE_REMINDERS.REMAINING_DAYS_VALUE', { value: days }) as string
  }

  protected initForm(): void {
    super.initForm()
    this.form.addControl('showArchived', new FormControl())
  }
}
